import _ from 'underscore';
import TransactionView from './epayment';
import Levenshtein from 'levenshtein';

import { FormModel } from '../models/form';

import Page from '../../base/collections/page';
import DialogService from '../../base/dialog_service';

const AccountOption = Marionette.LayoutView.extend({
  tagName: 'option',
  template: require('../templates/account_option.html'),

  attributes() {
    return {
      value: this.model.id,
      selected: this.getOption('selected')
    };
  }
});

const AttributeFilterOption = Marionette.LayoutView.extend({
  tagName: 'option',
  template: require('../templates/attribute_filter_option.html'),

  initialize() {
    const selectTextAttribute = this.getOption('selectTextAttribute');
    const selectText = this.model.get(selectTextAttribute);
    if (selectText) {
      this.model.set('select_title', selectText);
    }
  },

  attributes() {
    return {
      value: this._getValueOrEmpty(),
      selected: !this.model.id
    };
  },

  _getValueOrEmpty() {
    return this.model.get(this.getOption('selectValueAttribute')) || '';
  },

  templateHelpers() {
    return {
      selectTitle: this.model.get('select_title')
    };
  }
});

const LEVENSHTEIN_THRESHOLD = 3;

const Account = Marionette.CompositeView.extend({
  tagName: 'fieldset',
  className: 'block bankaccount',
  template: require('../templates/account.html'),
  childView: AccountOption,
  childViewContainer: 'select',
  childViewOptions(model) {
    const childId = model.get('id');
    const selectedId = this.model.get(this.getOption('field'));
    return {
      // If an account has been selected, the selected child is the one whose
      // ID matches the account.
      // Otherwise we just assume there's only one child with a falsey ID and
      // select that one.
      selected: selectedId ? childId === selectedId : !childId
    };
  },

  ui: {
    account: 'select'
  },

  events: {
    'change @ui.account': 'onChangeAccount'
  },

  collectionEvents: {
    sync: 'addEmpty'
  },

  initialize() {
    const listUrl = this.getOption('listUrl');
    this.collection.url = listUrl;
    this.collection.search_params = this._setSearchParams();
    this.collection.fetch({
      headers: {
        Authorization: `JWT ${this.getOption('token')}`
      }
    });
  },

  templateHelpers() {
    return {
      legendText: this.getOption('legendText'),
      settlementWarning: this._settlementWarning(),
      disableToggle: this._disableToggle()
    };
  },

  onUpdateList() {
    if (this.getOption('costcentre')) {
      this.pickBestAccount();
    }

    this.render();
  },

  _settlementWarning() {
    if (this.getOption('costcentre')) {
      return (
        '<img src="/static/images/icon-info.gif" alt="Important Info" />' +
        ' This account will only be used for transactions without an account.'
      );
    }

    const queriedType = this.getOption('queryModel').get('transaction_type');
    if (!queriedType) {
      return (
        '<img src="/static/images/icon-error.gif" alt="Important Info" />' +
        ' Settlement account will only be used for imported ePayments.'
      );
    }
  },

  _setSearchParams() {
    const params = { is_active: true };
    return params;
  },

  _disableToggle() {
    const queriedType = this.getOption('queryModel').get('transaction_type');
    if (this.getOption('costcentre') || !queriedType || queriedType == 'epay') {
      return '';
    } else {
      return 'disabled';
    }
  },

  onChangeAccount(event) {
    const costcentreName = event.target.selectedOptions[0].innerText.trim();
    const costcentreId = parseInt(event.target.value);
    const defaultCostcentreOptions = $('.costcentre_match select option[value=""]')

    this.model.set(this.getOption('field'), costcentreId);
    this.model.set(this.getOption('field') + "Name", costcentreName);

    if (costcentreId) {
      defaultCostcentreOptions.text(costcentreName);
    } else {
      defaultCostcentreOptions.text('-- Default Account --');
    }
  },

  addEmpty() {
    if (this.getOption('costcentre')) {
      this.collection.unshift({
        title: '-- Select Account --',
        id: ''
      });
    } else {
      if(this.collection.length > 0)
        this.model.set('settlement_account', this.collection.first().id);
    }
  },

  pickBestAccount() {
    if (this.getOption('costcentre')) {
      const queriedDescription = this.getOption('queryModel').get(
        'description'
      );

      if (queriedDescription) {
        const selectedIndex = this.collection.reduce(
          (memo, model, index) => {
            if (!model.get('name')) {
              return memo;
            }

            const { distance } = new Levenshtein(
              queriedDescription,
              model.get('name')
            );
            return distance < memo[1] ? [index, distance] : memo;
          },
          [-1, LEVENSHTEIN_THRESHOLD]
        )[0];

        if(selectedIndex != -1) {
          this.model.set(this.getOption('field'), selectedIndex);
        }
      }
    }
  }
});

const AttributeFilter = Marionette.CompositeView.extend({
  template: require('../templates/attribute_filter.html'),
  childView: AttributeFilterOption,
  childViewContainer: 'select',

  ui: {
    dropdown: 'select'
  },

  templateHelpers() {
    const queryField = this.getOption('queryField');
    return {
      selectId: `${queryField}_select_id`,
      labelText: this.getOption('labelText')
    };
  },

  initialize() {
    const listUrl = this.getOption('listUrl');
    this.collection.url = listUrl;
    this.collection.fetch({
      success: collection => {
        collection.unshift({
          select_title: this.getOption('placeholderText'),
          id: ''
        });
      }
    });
  }
});

/** The filters for the form to help users import subsets of information.
 */
const Filters = Marionette.LayoutView.extend({
  tagName: 'fieldset',
  className: 'block bankaccount',
  template: require('../templates/filters.html'),
  filters: [],

  regions: {
    descriptions: '.descriptions_filter-hook',
    transaction_types: '.transaction_type_filter-hook'
  },

  events: {
    'change .descriptions_filter-hook': 'setDescriptionFilter',
    'change .transaction_type_filter-hook': 'setTransactionTypeFilter',
    'click .button': 'runFilter'
  },

  runFilter() {
    this.collection.reset();
    this.triggerMethod('filter');
  },

  setDescriptionFilter() {
    this.setFilter('descriptions');
  },

  setTransactionTypeFilter() {
    this.setFilter('transactionType');
  },

  setFilter(filter) {
    filter = this.filters[filter];

    const queryField = filter.getOption('queryField');
    const selectedIndex = filter.ui.dropdown.prop('selectedIndex');

    this.getOption('queryModel').set(
      queryField,
      filter.ui.dropdown[0].options[selectedIndex].value
    );
  },

  onShow() {
    const links = this.getOption('links');

    const descriptionsView = new AttributeFilter({
      collection: new Backbone.Collection(),
      listUrl: links.descriptions,
      labelText: 'Description',
      childViewOptions: {
        selectTextAttribute: 'description',
        selectValueAttribute: 'description'
      },
      queryField: 'description',
      placeholderText: '-- All Transactions --',
      model: this.model
    });
    const transactionTypeView = new AttributeFilter({
      collection: new Backbone.Collection(),
      listUrl: links.transaction_types,
      labelText: 'Transaction type',
      childViewOptions: {
        selectTextAttribute: 'transaction_type_name',
        selectValueAttribute: 'transaction_type'
      },
      queryField: 'transaction_type',
      placeholderText: '-- All Transactions --',
      model: this.model
    });

    this.filters = {
      descriptions: descriptionsView,
      transactionType: transactionTypeView
    };

    this.showChildView('descriptions', descriptionsView);
    this.showChildView('transaction_types', transactionTypeView);
  }
});

const ActionView = Marionette.LayoutView.extend({
  template: require('../templates/actions.html')
});

const NoPendingView = Marionette.LayoutView.extend({
  template: require('../templates/no_pending.html'),
  className: 'block'
});

export default Marionette.LayoutView.extend({
  template: require('../templates/layout.html'),

  regions: {
    costcentre: '.costcentre-hook',
    settlement_account: '.settlement_account-hook',
    filters: '.filters-hook',
    list: '.list-hook',
    actions: '.actions-hook',
    no_pending: '.no_pending-hook'
  },

  ui: {
    save: '.import',
    clear: '.clear-import'
  },

  events: {
    'click @ui.save': 'importEPayments'
  },

  triggers: {
    'click @ui.clear': 'clear'
  },

  childEvents: {
    filter: 'filterNotify'
  },

  modelEvents: {
    sync: 'importSuccess'
  },

  initialize() {
    this.model.url = this.getOption('links').epayment;
    this.queryModel = new Backbone.Model({
      description: '',
      transaction_type: ''
    });

    const links = this.getOption('links');

    this.collection = new Page([], {
      urlBase: links.epayment,
      model: Backbone.Model.extend({
        isEpayment() {
          return this.get('transaction_type') == 'epay';
        }
      })
    });

    this.collection.fetch({
      success: collection => {
        if (collection.length) {
          this.showTransactionViews();
        } else {
          this.showNoPending();
        }
      }
    });
  },

  showTransactionViews() {
    const links = this.getOption('links');

    const list = new TransactionView({
      links,
      collection: this.collection,
      model: this.model,
      queryModel: this.queryModel
    });

    const costcentreView = new Account({
      collection: new Page([], { state: { pageSize: 500 } }),
      listUrl: links.costcentre,
      legendText: 'Default Account',
      field: 'costcentre',
      model: this.model,
      queryModel: this.queryModel,
      costcentre: true,
      token: this.getOption('token')
    });
    const bankView = new Account({
      collection: new Page([], { state: { pageSize: 500 } }),
      listUrl: links.settlement_account,
      legendText: 'Settlement account',
      field: 'settlement_account',
      model: this.model,
      queryModel: this.queryModel,
      costcentre: false,
      token: this.getOption('token')
    });

    const formModel = new FormModel();

    const filtersView = new Filters({
      links,
      model: formModel,
      collection: this.model.get('epayments'),
      queryModel: this.queryModel
    });

    const actionView = new ActionView();

    this.showChildView('costcentre', costcentreView);
    this.showChildView('settlement_account', bankView);
    this.showChildView('filters', filtersView);
    this.showChildView('actions', actionView);
    this.showChildView('list', list);
  },

  showNoPending() {
    const noPendingView = new NoPendingView();
    this.showChildView('no_pending', noPendingView);
  },

  templateHelpers() {
    return {
      dashboard: '/'
    };
  },

  onClear() {
    Backbone.sync('delete', this.model, {
      success: () => {
        window.messages.add('Cleared transactions');
        this.render();
      }
    });
  },

  importEPayments() {
    const costcentre = this.model.get('costcentre');
    let requireCostcentre = false;

    this.model.get('epayments').each(function (epayment) {
      if (isNaN(epayment.get('costcentre'))) {
        requireCostcentre = true;
      }
    });

    if (requireCostcentre && !$('.costcentre-hook select').val()) {
      const contactSelects = $('.match select')
      contactSelects.each(function (selectElement) {
        const thisContactSelect = $(contactSelects[selectElement]);

        if (thisContactSelect.val()) {
          const costcentreSelect = thisContactSelect.parents(
            'tr').find('.costcentre_match select');

          if (costcentreSelect.val() === '') {
            thisContactSelect.val('');
            thisContactSelect.trigger('change');
          }
        }
      });

      requireCostcentre = false
    }

    if(costcentre || !requireCostcentre) {
      const importConfirmView = new ImportConfirmView({model: this.model});
      const dialog = DialogService.show(importConfirmView);

      dialog.on('close', () => {
        dialog.close();
      });
      importConfirmView.on('saveImports', model => {
        dialog.close();
        this.model.save();
      });
    }else{
      window.messages.add('Please Select a default Account', 'error')
    }

  },

  importSuccess() {
    const transactions = this.model.get('epayments');
    const count = transactions.length;
    const message_suffix = count == 1 ? 'transaction' : 'transactions';
    window.messages.add(`Imported ${count} ${message_suffix}`);

    const transaction_ids = _.map(
      transactions,
      transaction => transaction.transaction
    );
    const collection = this.getRegion('list').currentView.collection;

    const has_cash = transaction_ids.reduce((memo, transaction) => {
      const matching_transaction = collection.get(transaction);
      return memo || !matching_transaction.isEpayment();
    }, false);

    if (!this.queryModel.get('transaction_type') && has_cash) {
      window.messages.add(
        'Settlement account was not used for imported ' + 'cash or cheques'
      );
    }

    this.model.clearEpayments();

    this.getRegion('list').currentView.triggerMethod('request:success');
  },

  filterNotify() {
    const costcentre = this.getRegion('costcentre').currentView;
    const settlement = this.getRegion('settlement_account').currentView;
    const list = this.getRegion('list').currentView;

    costcentre.triggerMethod('update:list');
    settlement.triggerMethod('update:list');
    list.triggerMethod('request:success');
  }
});

const ImportConfirmView = Marionette.LayoutView.extend({
  template: require('../templates/import_confirmation_dialog.html'),

  ui: {
    upload: '.saveButton',
    button: '.button',
  },

  events: {
    'click @ui.upload': 'saveClicked'
  },

  saveClicked() {
    this.ui.button.prop('disabled', 'disabled');
    this.trigger('saveImports');
  },

  templateHelpers() {
    return {
      costcentreName: this._getCostcentreName(),
    };
  },

  _getCostcentreName() {
    let costcentreName = 'your chosen account(s)';

    const chosenCostcentreId = this.model.get('costcentre');
    if (chosenCostcentreId) {
      const chosenCostcentreName = this.model.get('costcentreName');

      if (chosenCostcentreId) {
        costcentreName = '\'' + chosenCostcentreName + '\'';
      }
    }

    return costcentreName;
  }
});



// WEBPACK FOOTER //
// ./epayment_import/views/list.js